import { Link } from "react-router-dom";
import type { FarmPerformanceRow } from "@/pages/admin/dashboard/dashboardData";
import { formatRupees } from "@/pages/admin/format";

const STATUS_CHIP: Record<string, string> = {
  Active: "bg-secondary-100 text-secondary-700 border-secondary-200",
  Limited: "bg-accent-100 text-accent-900 border-accent-300",
  Paused: "bg-background-200 text-foreground-600 border-background-300",
};

interface FarmPerformanceTableProps {
  rows: FarmPerformanceRow[];
}

export default function FarmPerformanceTable({ rows }: FarmPerformanceTableProps) {
  return (
    <section className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-5">
        <div>
          <h2 className="font-heading text-xl text-foreground-950 font-semibold">Farm performance</h2>
          <p className="text-sm text-foreground-600 mt-1">Top farms by settlement value this period</p>
        </div>
        <Link
          to="/admin/farm-performance"
          className="text-xs font-semibold text-primary-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          Full report
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="border-t border-background-200 py-12 text-center text-sm text-foreground-500">
          No farm performance data yet.
        </div>
      ) : (
        <>
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto border-t border-background-200">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-background-100 border-b border-background-200">
              {["Farm", "Bookings", "Visitors", "Revenue", "Rating", "Utilization", "Status"].map((head) => (
                <th
                  key={head}
                  className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500 whitespace-nowrap"
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200">
            {rows.map((row) => (
              <tr key={row.farm} className="hover:bg-background-100 transition-colors">
                <td className="px-5 py-4">
                  <p className="text-sm font-semibold text-foreground-950">{row.farm}</p>
                </td>
                <td className="px-5 py-4 text-sm text-foreground-700">{row.bookings}</td>
                <td className="px-5 py-4 text-sm text-foreground-700">{row.visitors}</td>
                <td className="px-5 py-4 text-sm text-foreground-950 font-medium whitespace-nowrap">
                  {formatRupees(row.revenue)}
                </td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center gap-1 text-sm text-foreground-800">
                    <i className="ri-star-fill text-accent-500"></i>
                    {row.rating}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 rounded-full bg-background-100 overflow-hidden">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${row.utilization}%` }}></div>
                    </div>
                    <span className="text-xs text-foreground-600 w-8">{row.utilization}%</span>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${STATUS_CHIP[row.status]}`}
                  >
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden border-t border-background-200 divide-y divide-background-200">
        {rows.map((row) => (
          <div key={row.farm} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-semibold text-foreground-950">{row.farm}</p>
              <span
                className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${STATUS_CHIP[row.status]}`}
              >
                {row.status}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3 text-xs text-foreground-600">
              <span>Bookings: <span className="text-foreground-900 font-semibold">{row.bookings}</span></span>
              <span>Visitors: <span className="text-foreground-900 font-semibold">{row.visitors}</span></span>
              <span>Revenue: <span className="text-foreground-900 font-semibold">{formatRupees(row.revenue)}</span></span>
              <span className="inline-flex items-center gap-1">
                Rating: <i className="ri-star-fill text-accent-500"></i>
                <span className="text-foreground-900 font-semibold">{row.rating}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div className="flex-1 h-2 rounded-full bg-background-100 overflow-hidden">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${row.utilization}%` }}></div>
              </div>
              <span className="text-xs text-foreground-600">{row.utilization}%</span>
            </div>
          </div>
        ))}
      </div>
        </>
      )}
    </section>
  );
}